/**
 * recently-viewed-products-section.js
 *
 * Web component pour la section "Produits récemment consultés".
 * Charge les cartes produit via la Section Rendering API sur /products/{handle},
 * extrait chaque carte et l'insère dans la grille.
 */
import { RecentlyViewed } from './recently-viewed-products.js';
import { sectionRenderer } from '@theme/section-renderer';

class RecentlyViewedProductsSection extends HTMLElement {
  connectedCallback() {
    this.#loadProducts();
  }

  async #loadProducts() {
    const sectionId = this.dataset.sectionId;
    const maxProducts = parseInt(this.dataset.maxProducts || '4', 10);

    const resourceList = this.querySelector('[data-recently-viewed-resource-list]');
    const skeleton = this.querySelector('[data-recently-viewed-skeleton]');

    if (!sectionId || !resourceList) {
      return;
    }

    const handles = RecentlyViewed.getHandles();
    const currentHandle = this.dataset.productHandle;
    const handlesToFetch = handles.filter((h) => h !== currentHandle);

    if (handlesToFetch.length === 0) {
      return;
    }

    const root =
      (typeof window !== 'undefined' &&
        /** @type {{ routes?: { root?: string } }} */ (window.Shopify)?.routes?.root) ??
      '/';

    /** @type {HTMLElement[]} */
    const cards = [];

    for (const handle of handlesToFetch) {
      if (cards.length >= maxProducts) break;

      try {
        const productUrl = new URL(`${root}products/${handle}`, window.location.origin);
        const sectionHTML = await sectionRenderer.getSectionHTML(sectionId, false, productUrl);
        const card = this.#extractCardFromSectionHTML(sectionHTML);

        if (card instanceof HTMLElement) {
          cards.push(card);
        }
      } catch {
        RecentlyViewed.removeByHandle(handle);
      }
    }

    if (cards.length === 0) {
      return;
    }

    if (skeleton instanceof HTMLElement) {
      skeleton.hidden = true;
    }

    this.dataset.hasProducts = 'true';
    resourceList.dataset.hasProducts = 'true';

    if (this.dataset.layoutType === 'carousel') {
      this.#insertCardsInCarousel(resourceList, cards);
      return;
    }

    this.#insertCards(resourceList, cards);
  }

  #insertCards(resourceList, cards) {
    resourceList.replaceChildren();

    const fragment = document.createDocumentFragment();
    for (const card of cards) {
      fragment.appendChild(card);
    }
    resourceList.appendChild(fragment);
  }

  #insertCardsInCarousel(resourceList, cards) {
    const slideshow = resourceList.querySelector('slideshow-component');
    if (!(slideshow instanceof HTMLElement)) {
      this.#insertCards(resourceList, cards);
      return;
    }

    const rebuiltSlideshow = slideshow.cloneNode(true);
    const scroller = rebuiltSlideshow.querySelector('slideshow-slides[ref="scroller"]');

    if (!(scroller instanceof HTMLElement)) {
      this.#insertCards(resourceList, cards);
      return;
    }

    scroller.replaceChildren();

    const fragment = document.createDocumentFragment();
    cards.forEach((card, index) => {
      const slide = document.createElement('slideshow-slide');
      slide.setAttribute('ref', 'slides[]');
      slide.setAttribute('class', 'resource-list__slide');
      slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
      slide.setAttribute('slide-id', `recently-viewed-slide-${index}`);
      slide.appendChild(card);
      fragment.appendChild(slide);
    });

    scroller.appendChild(fragment);
    slideshow.replaceWith(rebuiltSlideshow);
  }

  #extractCardFromSectionHTML(sectionHTML) {
    const doc = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const card = doc.querySelector('[data-recently-viewed-api-card]');

    if (!card) {
      return null;
    }

    const cloned = card.cloneNode(true);
    return cloned instanceof HTMLElement ? cloned : null;
  }
}

if (!customElements.get('recently-viewed-products-section')) {
  customElements.define('recently-viewed-products-section', RecentlyViewedProductsSection);
}
